import React, { useCallback, useState } from 'react';
import { useLocale } from 'locale';
import FilterSummaryButton from 'components/filters/styled/FilterSummaryButton';
import { MobileExpandedWrapper } from 'components/filters/MobileWrapper';
import { SmallText } from 'ds/components/typography';
import { ShowAt } from 'consts/breakpoints';
import { FiltersSingleComponentsMap, config as filtersConfig } from 'components/filters/config';
import RangeSelect, { SelectValues } from 'ds/components/input/RangeSelect';
import { CountWrapper, PricesMobileFilterWrapper, PricesSummaryFiltersWrapper } from './styled';
import { SectionTitle } from 'screens/AgentOfficePages/styled';
import FilterSection from 'components/filters/common/DropdownSection';
import { head, noop, uniq } from 'lodash';
import { PerformantCheckboxGroup } from 'components/filters/common/PerformantCheckbox';
import { PropertyType } from 'components/filters/types';
import { SliderRange } from 'ds/components/slider';
import RangeInputs from 'ds/components/input/RangeInputs';
import { DealType, MarketplaceType } from 'utils/entities';
import { ValuesTuple } from 'ds/components/slider/utils';
import { DEFAULT_PRICE_VALUE, FilterState } from '.';
import { isFilterActiveByType, makeCallAll, normalizeRoomsRange } from 'components/filters/utils';
import { connect } from 'react-redux';
import { setMobileDiscoveryDisabled, setMobileDiscoveryEnabled } from 'store/state/app/actions';


interface PricesSummaryFiltersProps {
  filterState: FilterState;
  setFilterState: React.Dispatch<React.SetStateAction<FilterState>>;
  count: number;
  applyFilter: () => void;
  applyCustomFilter: (filter: FilterState) => void;
  makeMobileDiscoveryDisabled: () => void;
  makeMobileDiscoveryEnabled: () => void;
}

enum ActiveFilter {
  Beds = 'beds',
  Price = 'price',
  PropertyType = 'propertyType',
  Mobile = 'mobile',
}

const PricesSummaryFilters: React.FC<PricesSummaryFiltersProps> = ({
  filterState,
  setFilterState,
  count,
  applyFilter,
  applyCustomFilter,
  makeMobileDiscoveryDisabled,
  makeMobileDiscoveryEnabled,
}) => {
  const { t } = useLocale();
  const [ activeFilter, setActiveFilter ] = useState<ActiveFilter>(null);
  const { beds, price, propertyType } = filterState;

  const marketplace = MarketplaceType.Commercial;
  const fConfig = filtersConfig[marketplace];

  const budgetByDealType = fConfig.budgetByDealType;
  const BEDROOMS_OPTIONS = fConfig.roomsOptions;
  const propertyTypes = uniq([ ...fConfig.propertyTypes, ...filtersConfig[MarketplaceType.Residential].propertyTypes ]);
  const filtersIcons = filtersConfig[MarketplaceType.Residential].filtersIcons;

  const checkboxLabelGetter = (value: PropertyType) => t('filters.propertyTypeOptions', { value, marketPlace: marketplace });

  const normalizedCommitedValue = normalizeRoomsRange(beds, MarketplaceType.Residential);
  const isBedsActive = isFilterActiveByType.roomsRange(beds, [ null, null ], marketplace);
  const isPriceActive = isFilterActiveByType.priceRange(price, DEFAULT_PRICE_VALUE, marketplace);
  const [ nomalizedFrom, normalizedTo ] = normalizedCommitedValue;
  const shortValueLabel = t('agentOffice.deals.filters.roomsShortValue', {
    isActive: isBedsActive,
    exactValue: nomalizedFrom === normalizedTo ? nomalizedFrom : null,
    isRange: nomalizedFrom !== null && normalizedTo !== null,
    bedsFrom: nomalizedFrom,
    bedsTo: normalizedTo,
  });

  const activeFilters = [ isBedsActive, isPriceActive, Boolean(propertyType.length) ].filter(Boolean).length;

  const setFilter = useCallback((value: PropertyType[] | ValuesTuple | SelectValues, key: keyof FilterState) => {
    setFilterState(prev => ({ ...prev, [key]: value }));
  }, [ setFilterState ]);

  const checkboxGroup = (
    <PerformantCheckboxGroup
      values={propertyType}
      options={propertyTypes}
      labelGetter={checkboxLabelGetter}
      valueSetter={(value) => setFilter(value, 'propertyType')}
    />
  );

  const options = BEDROOMS_OPTIONS.map((value) => ({
    value,
    label: t('filters.roomsOptions', { value, isCommercial: true }),
  }));

  const select = (
    <RangeSelect
      values={beds}
      options={options}
      onChange={(value) => setFilter(value, 'beds')}
    />
  );

  const txtShortValue = t('filters.priceShortValue', {
    isActive: false,
    priceFrom: null,
    priceTo: null,
  });

  const slider = (
    <SliderRange
      {...budgetByDealType.unitBuy.budgetSliderProps}
      values={price}
      onChange={(value) => setFilter(value, 'price')}
    />
  );

  const inputs = (
    <RangeInputs
      {...budgetByDealType.unitBuy.budgetSliderProps}
      value={price}
      onChange={(value) => setFilter(value, 'price')}
      isMoneyMode
    />
  );

  const handleCloseFilters = () => {
    setActiveFilter(null);
    makeMobileDiscoveryEnabled();
    applyFilter();
  };

  const handleResetFilters = (type: keyof FilterState) => {
    let newFilter: FilterState;
    switch (type) {
      case 'beds':
        newFilter = { ...filterState, beds: [ null, null ] };
        break;
      case 'price':
        newFilter = { ...filterState, price: DEFAULT_PRICE_VALUE };
        break;
      case 'propertyType':
        newFilter = { ...filterState, propertyType: [] };
        break;
    }
    setFilterState(newFilter);
    applyCustomFilter(newFilter);
  };

  const resetAllFilters = () => {
    setFilterState({
      beds: [ null, null ],
      price: DEFAULT_PRICE_VALUE,
      propertyType: [],
    });
  };

  const genFilterClick = (filter: ActiveFilter) => (e: React.MouseEvent) => {
    e.stopPropagation();
    makeMobileDiscoveryDisabled();
    setActiveFilter(filter);
  };

  const MobilePriceRange = FiltersSingleComponentsMap.commercial.priceRange;
  const MobilePropertyType = FiltersSingleComponentsMap.residential.propertyTypes;
  const MobileRoomsRange = FiltersSingleComponentsMap.commercial.roomsRange;

  return (
    <>
      <ShowAt to={1}>
        <SectionTitle style={{ marginBottom: 24 }}>{t('officePage.deals.title')}<CountWrapper>{count}</CountWrapper></SectionTitle>
        <PricesSummaryFiltersWrapper>
          <FilterSummaryButton filtersNumber={activeFilters} onClick={genFilterClick(ActiveFilter.Mobile)} />
          {activeFilter === ActiveFilter.Mobile ? (
            <MobileExpandedWrapper onClose={handleCloseFilters} onReset={makeCallAll(resetAllFilters, applyFilter)} onCommit={applyFilter}>
              <PricesMobileFilterWrapper>
                <MobilePriceRange
                  isMobile={true}
                  isInline={true}
                  type="priceRange"
                  resetValue={noop}
                  commitValues={noop}
                  setIntermediateValue={(value) => setFilter(value, 'price')}
                  resetAll={noop}
                  marketplace={marketplace}
                  initialValue={price}
                  intermediateValue={price}
                  dealType={DealType.Buy}
                  config={filtersConfig.commercial}
                  commitedValue={price}
                />
                <MobileRoomsRange
                  isMobile={true}
                  isInline={true}
                  type="roomsRange"
                  resetValue={noop}
                  commitValues={noop}
                  setIntermediateValue={(value) => setFilter(value, 'beds')}
                  resetAll={noop}
                  marketplace={MarketplaceType.Commercial}
                  initialValue={beds}
                  intermediateValue={beds}
                  dealType={DealType.Buy}
                  config={filtersConfig.commercial}
                  commitedValue={beds}
                />
                <MobilePropertyType
                  isMobile={true}
                  isInline={true}
                  type="propertyTypes"
                  resetValue={noop}
                  commitValues={noop}
                  setIntermediateValue={(value) => setFilter(value, 'propertyType')}
                  resetAll={noop}
                  marketplace={marketplace}
                  initialValue={propertyType}
                  intermediateValue={propertyType}
                  dealType={DealType.Buy}
                  config={{ ...filtersConfig.commercial, propertyTypes }}
                  commitedValue={propertyType}
                />
              </PricesMobileFilterWrapper>
            </MobileExpandedWrapper>
          ) : null}
        </PricesSummaryFiltersWrapper>
      </ShowAt>
      <ShowAt from={2}>
        <SectionTitle>{t('officePage.deals.title')}<CountWrapper>{count}</CountWrapper></SectionTitle>
        <PricesSummaryFiltersWrapper>
          <SmallText>{t('widget.prices.filterBy')}</SmallText>
          <FilterSection
            isClearButtonDisabled={!Boolean(propertyType.length)}
            type="propertyTypes"
            alignment="first"
            icon={filtersIcons.propertyTypes}
            onClear={() => handleResetFilters('propertyType')}
            label={t('filters.propertyType', { count: propertyType.length, value: checkboxLabelGetter(head(propertyType)) })}
            onSubmit={applyFilter}
            isActive={Boolean(propertyType.length)}
          >
            {checkboxGroup}
          </FilterSection>
          <FilterSection
            isClearButtonDisabled={beds[0] === null && beds[1] === null}
            type="roomsRange"
            alignment="first"
            isActive={isBedsActive}
            icon={filtersIcons.roomsRange}
            title={t('filters.rooms', { modeB: false })}
            label={shortValueLabel}
            onClear={() => handleResetFilters('beds')}
            onSubmit={applyFilter}
          >
            {select}
          </FilterSection>
          <FilterSection
            isClearButtonDisabled={price[0] === DEFAULT_PRICE_VALUE[0] && price[1] === DEFAULT_PRICE_VALUE[1]}
            type="priceRange"
            alignment="first"
            title={t('filters.priceRange.secondaryTitle')}
            icon={filtersIcons.price}
            onClear={() => handleResetFilters('price')}
            label={txtShortValue}
            onSubmit={applyFilter}
            isActive={isPriceActive}
          >
            {slider}
            {inputs}
          </FilterSection>
        </PricesSummaryFiltersWrapper>
      </ShowAt>
    </>
  );
};

const mapDispatchToProps = {
  makeMobileDiscoveryDisabled: setMobileDiscoveryDisabled,
  makeMobileDiscoveryEnabled: setMobileDiscoveryEnabled,
};

export default connect(null, mapDispatchToProps)(PricesSummaryFilters);
